import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Bot, Send, User, Loader2, AlertTriangle, Sparkles, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const AIAssistantPage = () => {
  const { api } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [systemCheck, setSystemCheck] = useState(null);
  const [systemCheckLoading, setSystemCheckLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await api().post('/ai/chat', { message: input });
      const assistantMessage = { role: 'assistant', content: response.data.response };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to get AI response';
      toast.error(errorMessage);
      setMessages(prev => [...prev, { role: 'error', content: errorMessage }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSystemCheck = async () => {
    setSystemCheckLoading(true);
    try {
      const response = await api().post('/ai/system-check');
      setSystemCheck(response.data);
      toast.success('System check completed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'System check failed');
    } finally {
      setSystemCheckLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestedPrompts = [
    "What's the current inventory health status?",
    "Are there any items below reorder point?",
    "Analyze production efficiency for active work orders",
    "What vulnerabilities exist in our supply chain?",
    "Suggest alternative vendors for low-stock items",
  ];

  return (
    <div className="space-y-6 animate-slideIn" data-testid="ai-assistant-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">AI Assistant</h1>
          <p className="text-slate-500 text-sm">Get insights and recommendations powered by GPT-5.2</p>
        </div>
        <Button
          onClick={handleSystemCheck}
          disabled={systemCheckLoading}
          variant="outline"
          className="rounded-sm"
          data-testid="system-check-btn"
        >
          {systemCheckLoading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Run System Check
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Chat Interface */}
        <Card className="lg:col-span-2 industrial-card flex flex-col h-[600px]">
          <CardHeader className="border-b border-slate-100 pb-3 flex-shrink-0">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bot className="w-5 h-5 text-orange-500" />
              Chat with AI
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
            {/* Messages Area */}
            <ScrollArea className="flex-1 p-4">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-400">
                  <Sparkles className="w-12 h-12 mb-4 text-orange-300" />
                  <p className="text-lg font-medium mb-2">How can I help you today?</p>
                  <p className="text-sm text-center max-w-md">
                    Ask me about inventory status, production analysis, supply chain optimization, or system vulnerabilities.
                  </p>
                  <div className="mt-6 space-y-2">
                    {suggestedPrompts.slice(0, 3).map((prompt, idx) => (
                      <button
                        key={idx}
                        onClick={() => setInput(prompt)}
                        className="block text-sm text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 px-4 py-2 rounded-sm transition-colors"
                      >
                        {prompt}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role !== 'user' && (
                        <div className={`w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 ${
                          msg.role === 'error' ? 'bg-red-100' : 'bg-orange-100'
                        }`}>
                          {msg.role === 'error' ? (
                            <AlertTriangle className="w-4 h-4 text-red-600" />
                          ) : (
                            <Bot className="w-4 h-4 text-orange-600" />
                          )}
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] px-4 py-3 rounded-sm ${
                          msg.role === 'user'
                            ? 'bg-slate-900 text-white'
                            : msg.role === 'error'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'bg-slate-100 text-slate-800'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      {msg.role === 'user' && (
                        <div className="w-8 h-8 rounded-sm bg-slate-200 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-slate-600" />
                        </div>
                      )}
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-sm bg-orange-100 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-orange-600" />
                      </div>
                      <div className="bg-slate-100 px-4 py-3 rounded-sm">
                        <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Input Area */}
            <div className="p-4 border-t border-slate-100">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about inventory, production, supply chain..."
                  className="flex-1 rounded-sm"
                  disabled={loading}
                  data-testid="ai-chat-input"
                />
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                  data-testid="ai-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Check Results */}
        <Card className="industrial-card h-[600px] flex flex-col">
          <CardHeader className="border-b border-slate-100 pb-3 flex-shrink-0">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              System Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-auto p-4">
            {systemCheckLoading ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-500">Running system check...</p>
                </div>
              </div>
            ) : systemCheck ? (
              <div className="space-y-4">
                <div className="text-xs text-slate-400">
                  Generated: {new Date(systemCheck.generated_at).toLocaleString()}
                </div>
                <div className="bg-slate-50 p-4 rounded-sm">
                  <p className="text-sm whitespace-pre-wrap">{systemCheck.analysis}</p>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-400">
                <AlertTriangle className="w-12 h-12 mb-4 text-slate-300" />
                <p className="text-sm text-center">
                  Click "Run System Check" to get a comprehensive analysis of your inventory and production system.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Prompts */}
      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <CardTitle className="text-lg">Suggested Questions</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2">
            {suggestedPrompts.map((prompt, idx) => (
              <button
                key={idx}
                onClick={() => setInput(prompt)}
                className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-sm transition-colors"
                data-testid={`suggested-prompt-${idx}`}
              >
                {prompt}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIAssistantPage;
